'use client';

import React, { useState, useEffect, useCallback } from 'react';
import type { PageType } from '@/features/Analytics';
import { TimeSlotsClient, useBooking } from '@/features/Booking';
import { useBookingModals } from '@/features/Booking/model/store/bookingModals';
import { MedcenterTabs } from '@/features/MedcenterTabs';
import { Map } from '@/widgets/Map';
import { useRouter } from '@/navigation';
import { SESSION_LAST_BOOKING_TIMESTAMP } from '@/shared/config/common/constants/storage';
import { useFetchBookingData } from '@/shared/hooks/api/useFetchBookingData';
import { useDoctor } from '../../model/store';
import { AddressMedcenter } from '../AddressMedcenter';
import { DoctorPageSchedule } from '../DoctorPageSchedule';
import { GoMedcenter } from '../GoMedcenter';
import type { IBookingDoctorData } from '@/entities/booking/model/types';
import type { IDoctorDetails } from '@/entities/doctor/model/types';
import type { IMedcenter } from '@/entities/medcenter/model/types';
interface Props {
  medcenters: IMedcenter[];
  doctor: IDoctorDetails;
  city: string;
  defaultActiveIndex: number;
  fullMap: boolean;
  bookingData: IBookingDoctorData[];
  pageType: PageType;
}
export const MedcenterTabsClient: React.FC<Props> = props => {
  const {
    medcenters,
    fullMap,
    bookingData: initialBookingData,
    defaultActiveIndex,
    doctor,
    city,
    pageType
  } = props;
  const router = useRouter();
  const {
    setMedcenterId,
    resetData: resetDoctorData,
    medcenterId
  } = useDoctor();
  const {
    setMedcenterId: setMedcenterBookingId,
    resetData: resetBookingData
  } = useBooking();
  const {
    setHasOnlineBooking
  } = useBooking();
  const {
    setVisibleBookingModal
  } = useBookingModals();
  const {
    fetchBookingData
  } = useFetchBookingData({
    medcenters: doctor.medcenters,
    doctorAlias: doctor.alias
  });
  const [currentMedcenter, setCurrentMedcenter] = useState<IMedcenter>(medcenters[defaultActiveIndex]);
  const [currentBooking, setCurrentBooking] = useState<IBookingDoctorData | null>(null);
  const [refreshedBookingData, setRefreshedBookingData] = useState<IBookingDoctorData[]>(initialBookingData);
  const [bookingCompletedTimestamp, setBookingCompletedTimestamp] = useState<string | null>(null);
  const refetchBookingData = useCallback(async () => {
    try {
      const freshBookingData = await fetchBookingData(medcenterId);
      if (!freshBookingData || freshBookingData.length === 0) return;
      setRefreshedBookingData(freshBookingData);
      if (currentMedcenter) {
        updateCurrentBooking(freshBookingData, currentMedcenter.id);
      }
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(SESSION_LAST_BOOKING_TIMESTAMP);
      }
    } catch (error) {
      console.error('Error refetching booking data:', error);
    }
  }, [doctor.alias, currentMedcenter]);
  const updateCurrentBooking = (bookingData: IBookingDoctorData[], medcenterId: number) => {
    const freshBooking = bookingData.find(item => item.medcenterId === medcenterId);
    if (!freshBooking) return;
    const {
      timeslotsIsEmpty,
      basedOn,
      schedule
    } = freshBooking.onlineBooking;
    if (timeslotsIsEmpty || basedOn === 'preferred_time' || schedule?.length === 0) return;
    setCurrentBooking(freshBooking);
  };
  const setMedcenterIdHandler = (id: number) => {
    setMedcenterId(id);
    setMedcenterBookingId(id);
  };
  const handleClick = (medcenter: IMedcenter) => {
    setMedcenterIdHandler(medcenter.id);
    setHasOnlineBooking(Boolean(medcenter.appointments?.online?.price));
    setCurrentMedcenter(medcenter);
  };
  const showFullMap = () => {
    router.push({
      pathname: `/${city}/doctor/${doctor.alias}`,
      query: {
        fullMap: true
      }
    });
  };
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const timestamp = sessionStorage.getItem(SESSION_LAST_BOOKING_TIMESTAMP);
      if (timestamp && timestamp !== bookingCompletedTimestamp) {
        setBookingCompletedTimestamp(timestamp);
        refetchBookingData();
        resetBookingData();
        setVisibleBookingModal(false);
      }
    }
  }, [refetchBookingData, resetBookingData, bookingCompletedTimestamp, setVisibleBookingModal]);
  useEffect(() => {
    if (typeof window !== 'undefined' && document) {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          const timestamp = sessionStorage.getItem(SESSION_LAST_BOOKING_TIMESTAMP);
          if (timestamp && timestamp !== bookingCompletedTimestamp) {
            setBookingCompletedTimestamp(timestamp);
            refetchBookingData();
            resetBookingData();
            setVisibleBookingModal(false);
          }
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [refetchBookingData, resetBookingData, bookingCompletedTimestamp, setVisibleBookingModal]);
  useEffect(() => {
    refetchBookingData();
  }, [refetchBookingData]);
  useEffect(() => {
    if (medcenters.length && !medcenterId) {
      setMedcenterIdHandler(medcenters[0].id);
      setHasOnlineBooking(Boolean(medcenters[0].appointments?.online?.price));
    }
  }, [medcenters, medcenterId]);
  useEffect(() => {
    if (!medcenterId && currentMedcenter) {
      setMedcenterIdHandler(currentMedcenter.id);
      setHasOnlineBooking(Boolean(currentMedcenter.appointments?.online?.price));
    }
    return () => {
      resetDoctorData();
      resetBookingData();
    };
  }, []);
  useEffect(() => {
    if (currentMedcenter) {
      const booking = refreshedBookingData.find(item => item.medcenterId === currentMedcenter.id);
      if (booking) {
        const {
          timeslotsIsEmpty,
          basedOn,
          schedule
        } = booking.onlineBooking;
        if (timeslotsIsEmpty || basedOn === 'preferred_time' || schedule?.length === 0) return;
        setCurrentBooking(booking);
      }
    }
  }, [currentMedcenter, refreshedBookingData]);
  return <>
      {medcenters.length > 1 && <MedcenterTabs medcenters={medcenters} defaultActiveIndex={defaultActiveIndex} medcenterClick={handleClick} />}

      {currentMedcenter.geo.address && <div className="mt-4">
          <Map geo={currentMedcenter.geo} showFullMap={showFullMap} visibleFullMap={fullMap} />
        </div>}

      <div className="mt-4 flex flex-wrap gap-3 md:items-center">
        {currentMedcenter.geo.address && <AddressMedcenter address={currentMedcenter.geo.address} />}

        <GoMedcenter link={{
        url: `/${doctor.city.alias}/medcenter/${currentMedcenter.alias}`,
        text: currentMedcenter.name
      }} data-sentry-element="GoMedcenter" data-sentry-source-file="MedcenterTabsClient.tsx" />
      </div>

      <div className="mt-4 empty:hidden">
        <DoctorPageSchedule medcenters={doctor.medcenters} bookingData={refreshedBookingData} data-sentry-element="DoctorPageSchedule" data-sentry-source-file="MedcenterTabsClient.tsx" />
      </div>

      <div className="mt-4 empty:hidden">
        {currentBooking?.onlineBooking?.schedule?.[0] && <TimeSlotsClient initialDay={currentBooking.onlineBooking.schedule[0]} bookingData={refreshedBookingData} doctorId={doctor.id} pageType={pageType} />}
      </div>
    </>;
};